import React, { useState, useRef, useEffect } from 'react';
import { User } from '../api/api';
import './UserSelector.css';

interface UserSelectorProps {
  users: User[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  excludeUserIds?: number[]; // Исключить пользователей из списка
}

const UserSelector: React.FC<UserSelectorProps> = ({
  users,
  selectedIds,
  onChange,
  placeholder = 'Выберите пользователей',
  label,
  hint,
  excludeUserIds = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Фильтруем пользователей, исключая указанных
  const filteredUsers = users.filter(user => !excludeUserIds.includes(user.id));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleUser = (userId: number) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter(id => id !== userId));
    } else {
      onChange([...selectedIds, userId]);
    }
  };

  const getSelectedNames = () => {
    if (selectedIds.length === 0) return placeholder;
    const names = selectedIds
      .map(id => filteredUsers.find(u => u.id === id)?.username)
      .filter(Boolean)
      .join(', ');
    return names.length > 50 ? names.substring(0, 50) + '...' : names;
  };

  return (
    <div className="user-selector-container" ref={containerRef}>
      {label && <label className="user-selector-label">{label}</label>}
      <div
        className={`user-selector-dropdown ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="user-selector-display">
          <span className={selectedIds.length === 0 ? 'placeholder' : ''}>
            {getSelectedNames()}
          </span>
          <span className="user-selector-arrow">{isOpen ? '▲' : '▼'}</span>
        </div>
      </div>
      {isOpen && (
        <div className="user-selector-list">
          {filteredUsers.length === 0 ? (
            <div className="user-selector-empty">Нет доступных пользователей</div>
          ) : (
            filteredUsers.map((user) => (
              <label key={user.id} className="user-selector-item">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                />
                <span>{user.username}</span>
              </label>
            ))
          )}
        </div>
      )}
      {hint && <small className="user-selector-hint">{hint}</small>}
    </div>
  );
};

export default UserSelector;

